import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import html2pdf from "html2pdf.js";
import { useDispatch, useSelector } from "react-redux";
import { fetchSalaries } from "../redux/slices/salarySlice";
import { Printer, Download, ArrowLeft } from "lucide-react";
import "../letterstyles/OfferLetter.css";

const OfferLetter = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const dispatch = useDispatch();

  const user = state?.user;
  const { salaryData } = useSelector((state) => state.salary);

  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      alert("No candidate data provided.");
      navigate("/");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!superAdminId || !user?.id) return;
    dispatch(fetchSalaries({ superAdminId, employeeId: user.id }));
  }, [superAdminId, user?.id, dispatch]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${superAdminId}`
        );
        const data = await response.json();

        if (response.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          }
          setCompany(data);
        } else {
          console.error("Failed to fetch company profile.");
        }
      } catch (err) {
        console.error("Error retrieving company details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (superAdminId) fetchCompany();
  }, [superAdminId]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Offer_Letter_${user?.userId || user?.fullName || "Candidate"}`,
  });

  const handleDownloadPDF = () => {
    const element = printRef.current;
    if (!element) return;

    const opt = {
      margin: 0,
      filename: `Offer_Letter_${user?.userId || user?.fullName || "Candidate"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2.5,
        useCORS: true,
        allowTaint: true,
        logging: false,
        letterRendering: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
      pagebreak: {
        mode: ["avoid-all", "css", "legacy"],
        avoid: [
          ".ol-doc-header",
          ".ol-doc-block",
          ".ol-doc-salary-section",
          ".ol-doc-sign-block",
          ".ol-doc-table tr",
        ],
      },
    };

    html2pdf().set(opt).from(element).save();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString("en-GB");
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "--";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (!user) return null;
  if (loading) {
    return (
      <div className="ol-doc-loading-screen">
        <p>Loading offer letter...</p>
      </div>
    );
  }

  const salary = Array.isArray(salaryData) ? salaryData[0] : salaryData;
  const basicSalary = Number(salary?.basic || 0);
  const hra = Number(salary?.hra || 0);
  const specialAllowance = Number(salary?.specialAllowance || 0);
  const monthlyGross = Number(salary?.totalSalary) || basicSalary + hra + specialAllowance;
  const annualGross = monthlyGross * 12;

  return (
    <div className="ol-doc-wrapper">
      <div className="ol-doc-action-bar">
        <button
          type="button"
          className="ol-doc-btn ol-doc-btn-back"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="ol-doc-action-group">
          <button
            type="button"
            className="ol-doc-btn ol-doc-btn-print"
            onClick={handlePrint}
          >
            <Printer size={16} /> Print Document
          </button>
          <button
            type="button"
            className="ol-doc-btn ol-doc-btn-download"
            onClick={handleDownloadPDF}
          >
            <Download size={16} /> Download PDF
          </button>
        </div>
      </div>

      <div className="ol-doc-sheet-container" ref={printRef}>
        <div className="ol-doc-sheet">
          <header className="ol-doc-header">
            <img
              src={company?.logoUrl || "/default-logo.png"}
              alt="Company Logo"
              className="ol-doc-logo"
            />
            <div className="ol-doc-company-details">
              <h2>{company?.name || ""}</h2>
              <p>
                <strong>Regd. Office:</strong> {company?.address || ""}
              </p>
              <p>
                <strong>CIN:</strong> {company?.cin || "N/A"}
              </p>
              <p>
                <strong>Email:</strong> {company?.email || "—"} |{" "}
                <strong>Contact:</strong> {company?.contact || "—"}
              </p>
            </div>
          </header>

          <div className="ol-doc-title-wrapper">
            <h1>Offer of Employment</h1>
          </div>

          <main className="ol-doc-body">
            <div className="ol-doc-date-row">
              <strong>Date:</strong> {formatDate(user.dateOfJoining)}
            </div>

            <div className="ol-doc-recipient-box">
              <p>To,</p>
              <p className="ol-doc-recipient-name">
                <strong>{user.fullName || user.name}</strong>
              </p>
              <p>{user.address || ""}</p>
              <p>
                {[user.district, user.state, user.pincode].filter(Boolean).join(", ")}
              </p>
            </div>

            <p className="ol-doc-subject-line">
              <strong>Subject:</strong> Offer of Employment for the position of{" "}
              <strong>{user.roleName || ""}</strong>
            </p>

            <p>
              <strong>Dear {user.fullName ? user.fullName.split(" ")[0] : user.name},</strong>
            </p>

            <p>
              We are pleased to extend this formal offer of employment for the position of{" "}
              <strong>{user.roleName || ""}</strong> at <strong>{company?.name}</strong>. Following
              our review, we were impressed by your experience and are confident that you will make
              significant contributions to our team.
            </p>

            <section className="ol-doc-block">
              <p className="ol-doc-section-title">
                <strong>1. Position & Appointment Details:</strong>
              </p>
              <ul className="ol-doc-list">
                <li>
                  <strong>Job Designation:</strong> {user.roleName || "—"}
                </li>
                <li>
                  <strong>Department / Branch:</strong> {user.branchName || user.branch || "—"}
                </li>
                <li>
                  <strong>Effective Joining Date:</strong> {formatDate(user.dateOfJoining)}
                </li>
                <li>
                  <strong>Place of Posting:</strong> {user.bankBranch || user.branchName || "Corporate Office"}
                </li>
              </ul>
            </section>

            <section className="ol-doc-block ol-doc-salary-section">
              <p className="ol-doc-section-title">
                <strong>2. Compensation Schedule:</strong>
              </p>
              <table className="ol-doc-table">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Monthly Entitlement (₹)</th>
                    <th>Annual Value (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Basic Salary</td>
                    <td className="ol-doc-num-col">₹{formatCurrency(basicSalary)}</td>
                    <td className="ol-doc-num-col">₹{formatCurrency(basicSalary * 12)}</td>
                  </tr>
                  <tr>
                    <td>House Rent Allowance (H.R.A.)</td>
                    <td className="ol-doc-num-col">₹{formatCurrency(hra)}</td>
                    <td className="ol-doc-num-col">₹{formatCurrency(hra * 12)}</td>
                  </tr>
                  <tr>
                    <td>Special Allowance</td>
                    <td className="ol-doc-num-col">₹{formatCurrency(specialAllowance)}</td>
                    <td className="ol-doc-num-col">₹{formatCurrency(specialAllowance * 12)}</td>
                  </tr>
                  <tr className="ol-doc-total-row">
                    <td>Total Gross Compensation</td>
                    <td className="ol-doc-num-col">₹{formatCurrency(monthlyGross)}</td>
                    <td className="ol-doc-num-col">₹{formatCurrency(annualGross)}</td>
                  </tr>
                </tbody>
              </table>
              <small className="ol-doc-note">
                * Compensation is subject to mandatory tax deductions and statutory deductions as applicable.
              </small>
            </section>

            <section className="ol-doc-block">
              <p className="ol-doc-section-title">
                <strong>3. Terms & Conditions:</strong>
              </p>
              <ul className="ol-doc-list">
                <li>This offer is contingent upon satisfactory background evaluation and verification of submitted documentation.</li>
                <li>You will be required to execute standard company confidentiality and code-of-conduct agreements.</li>
                <li>Detailed terms of service, benefits, and responsibilities will be provided upon your formal joining.</li>
              </ul>
            </section>

            <p className="ol-doc-closing-text">
              Please sign and return the duplicate copy of this letter within five (5) business days to confirm your acceptance.
            </p>

            <div className="ol-doc-sign-block">
              <div className="ol-doc-sign-col">
                <p>
                  <strong>For {company?.name || ""}</strong>
                </p>
                <div className="ol-doc-sign-spacer" />
                <p>
                  <strong>Authorized Signatory</strong>
                </p>
                <p className="ol-doc-sign-sub">Human Resources</p>
              </div>

              <div className="ol-doc-sign-col ol-doc-sign-right">
                <p>
                  <strong>Accepted and Agreed:</strong>
                </p>
                <div className="ol-doc-sign-spacer" />
                <p>
                  <strong>Signature:</strong> _________________________
                </p>
                <p>
                  <strong>Date:</strong> ______________________________
                </p>
              </div>
            </div>
          </main>

          <footer className="ol-doc-footer">
            <p>
              {company?.name} | CIN: {company?.cin || "N/A"}
            </p>
            <p>
              Email: {company?.email || "N/A"} | Address: {company?.address || "N/A"}
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default OfferLetter;